// Presentation helpers only. Access is always checked by the existing APIs.
import { getTeamRole, isAppAdmin, isOrgAdminForTeam, teamRoleLabelSv } from './roles';

// Leader roles remain distinct from player roles. A linked
// child is an optional family view and must never replace this workspace by
// default for a mixed leader/guardian account.
export const LEADER_ROLES = new Set([
  'admin', 'team_admin', 'head_coach', 'assistant_coach',
  'coach', 'team_manager',
]);

export const contextKey = (profileId, teamId) =>
  typeof profileId === 'string' && profileId && typeof teamId === 'string' && teamId
    ? `${profileId}:${teamId}` : null;

export const parentStorageKey = (userId) => `trainnova:parent-context:${userId}`;

export function normalizeGuardianLinks(rows) {
  const seen = new Set();
  return (Array.isArray(rows) ? rows : []).filter(link => {
    const key = contextKey(link?.player_profile_id, link?.team_id);
    if (!key || seen.has(key) || link.is_active === false || link.pending_invitation_id ||
        ['inactive', 'archived', 'deleted', 'disabled', 'deactivated'].includes(link.status)) return false;
    seen.add(key);
    return true;
  });
}

export function pickParentContext(links, savedKey, teamId, options = {}) {
  const inTeam = links.filter(link => link.team_id === teamId);
  if (options.disabled) return null;
  // An explicit choice is session-scoped by ParentProvider. Never substitute
  // a sibling if that relationship disappears during a refresh.
  if (options.explicitKey) return inTeam.find(link =>
    contextKey(link.player_profile_id, link.team_id) === options.explicitKey) || null;
  if (options.preferOwnTeam) return null;
  return inTeam.find(link => contextKey(link.player_profile_id, link.team_id) === savedKey)
    || inTeam[0] || null;
}

export function availableTeams(teams) {
  const seen = new Set();
  return (Array.isArray(teams) ? teams : []).filter(team => {
    if (!team?.id || seen.has(team.id) || team.is_active === false ||
        ['archived', 'deleted', 'disabled', 'deactivated'].includes(team.status)) return false;
    seen.add(team.id);
    return true;
  });
}

export function accountTeamRoleLabel(user, team) {
  if (!team?.id) return '';
  const role = getTeamRole(user, team.id);
  if (role) {
    const label = teamRoleLabelSv(role);
    return isAppAdmin(user) ? `${label} · Plattformsadmin` : label;
  }
  if (isOrgAdminForTeam(user, team.id, team)) return 'Föreningsadmin';
  if (isAppAdmin(user)) return 'Plattformsadmin';
  return 'Ingen lagroll';
}

export function isLeaderAccountForTeam(user, team) {
  if (!team?.id) return false;
  const role = getTeamRole(user, team.id);
  return Boolean(role && LEADER_ROLES.has(role.toLowerCase()));
}

export function hasLeaderWorkspace(user, team) {
  if (!team?.id) return isAppAdmin(user);
  return isLeaderAccountForTeam(user, team)
    || isOrgAdminForTeam(user, team.id, team)
    || isAppAdmin(user);
}

export function hasOwnTeamWorkspace(user, team) {
  return getTeamRole(user, team?.id) === 'player' || hasLeaderWorkspace(user, team);
}

export function contextDisplayLabel(active, activeTeam, user) {
  if (active) return active.player_name || 'Barnvy';
  if (getTeamRole(user, activeTeam?.id) === 'player') return 'Mitt spelarläge';
  if (hasOwnTeamWorkspace(user, activeTeam)) return 'Mitt ledarläge';
  return getTeamRole(user, activeTeam?.id) === 'parent' ? 'Mina barn' : 'Mina lag';
}

export function childCount(links) {
  // Only an explicit linked account can identify the same player across teams.
  // Never merge siblings using their name or their guardian's inbox.
  return new Set(links.map(link => link.player_user_id
    ? `account:${link.player_user_id}` : `profile:${link.player_profile_id}`)).size;
}
